import FilterParameterDto from "../../classes/dtos/datasourcemodule/FilterParameterDto";
import FilterParameterValueDto from "../../classes/dtos/datasourcemodule/FilterParameterValueDto";
import FilterParameterVariableDto from "../../classes/dtos/datasourcemodule/FilterParameterVariableDto";
import FilterConstantValue from "../../classes/dtos/datasourcemodule/FilterConstantValue";
import ComparisonOperators from "../../classes/consts/DataSourceModule/ComparisonOperators";

const OPERATOR_SYMBOLS: Record<number, string> = {
    [ComparisonOperators.LessThan]: "<",
    [ComparisonOperators.GreaterThan]: ">",
    [ComparisonOperators.LessThanOrEqualTo]: "<=",
    [ComparisonOperators.GreaterThanOrEqualTo]: ">=",
    [ComparisonOperators.EqualTo]: "=",
    [ComparisonOperators.NotEqualTo]: "<>",
};

export function comparisonOperatorSymbol(operator: ComparisonOperators): string {
    return OPERATOR_SYMBOLS[operator] ?? "?";
}

export interface ComparisonOperatorOption {
    value: ComparisonOperators;
    label: string;
}

const ALL_COMPARISON_OPERATORS: ComparisonOperators[] = [
    ComparisonOperators.EqualTo,
    ComparisonOperators.NotEqualTo,
    ComparisonOperators.LessThan,
    ComparisonOperators.LessThanOrEqualTo,
    ComparisonOperators.GreaterThan,
    ComparisonOperators.GreaterThanOrEqualTo,
];

export function comparisonOperatorOptions(): ComparisonOperatorOption[] {
    return ALL_COMPARISON_OPERATORS.map((value) => ({ value, label: OPERATOR_SYMBOLS[value] }));
}

/**
 * Operator options allowed by a source's filtering capabilities (a single bit-mask of
 * ComparisonOperators, see FilteringCapabilitiesDto.Operators). A falsy / empty mask means the
 * capabilities are unknown (not loaded, or the field isn't in the list) — fall back to all six.
 */
export function comparisonOperatorOptionsFromMask(mask: number): ComparisonOperatorOption[] {
    if (!mask) {
        return comparisonOperatorOptions();
    }
    const allowed = ALL_COMPARISON_OPERATORS.filter((value) => (mask & value) === value);
    return (allowed.length > 0 ? allowed : ALL_COMPARISON_OPERATORS).map((value) => ({ value, label: OPERATOR_SYMBOLS[value] }));
}

// The right-hand side of a row filter: a constant (FilterParameterValueDto.Value, a typed JSON
// scalar — shown without its JSON quotes) or a variable technical name
// (FilterParameterVariableDto.TechnicalName).
export function rowFilterValue(filter: FilterParameterDto): string {
    if (filter instanceof FilterParameterValueDto) {
        return FilterConstantValue.displayLoose(filter.Value);
    }
    if (filter instanceof FilterParameterVariableDto) {
        return filter.TechnicalName;
    }
    return "";
}

/**
 * Short one-line summary of a row's filters, e.g. "= amount, <> DONE". A variable with no operator is
 * a complex-mode declaration (named in the popup, not yet wired into the Filters tab) and shows as
 * "{name}".
 */
export function describeRowFilters(filters: FilterParameterDto[]): string {
    return filters
        .map((f) => {
            const value = rowFilterValue(f);
            if (f instanceof FilterParameterVariableDto && !f.Operator) {
                return value ? `{${value}}` : "";
            }
            return `${comparisonOperatorSymbol(f.Operator)} ${value}`.trim();
        })
        .filter((s) => s.length > 0)
        .join(", ");
}

/**
 * Short one-line summary of a row's declared filter variables (complex mode), e.g. "{amount}, {status}".
 * A declared variable is only NAMED in the FILTER popup; its expression is authored in the Filters tab.
 */
export function describeDeclaredVariables(declaredVariables: { TechnicalName: string }[]): string {
    return declaredVariables
        .map((d) => d.TechnicalName.trim())
        .filter((n) => n.length > 0)
        .map((n) => `{${n}}`)
        .join(", ");
}
